import type { ReactNode } from 'react';
import './Breadcrumbs.css';

export interface Crumb {
  label: ReactNode;
  /** A destination. The last crumb has none — it is the current page. */
  href?: string;
  onSelect?: () => void;
}

export interface BreadcrumbsProps {
  items: Crumb[];
  /**
   * What the trail is for. Defaults to "Breadcrumb", the conventional name —
   * override only when a page has two trails, which no current screen does.
   */
  label?: string;
}

/**
 * Breadcrumb trail (design-system catalogue, Tier 2).
 *
 * **16 of the prototype's 18 page files**, and the cheapest of the three
 * universal components: `AppShell` already provides the slot, the
 * `<nav aria-label>` wrapper and the responsive drop rule — only the trail itself
 * was missing (prototype/CENSUS.md §2a item 3).
 *
 * **Intermediate crumbs are links here, and are plain `<span>`s in the
 * prototype.** That is a deliberate divergence, not an oversight: a breadcrumb
 * that cannot be followed is decoration, and the entire reason 08 §2 requires one
 * on every detail page is to get back up the hierarchy. The prototype is a
 * click-through demo with no router, so its spans cost it nothing.
 *
 * Semantics: an ordered list inside a `nav`, because the order *is* the meaning,
 * and `aria-current="page"` on the final crumb so a screen-reader user knows
 * which end of the trail they are standing on. The separator is `aria-hidden` — a
 * reader announcing "chevron" three times adds nothing the list structure does not
 * already convey.
 */
export function Breadcrumbs({ items, label = 'Breadcrumb' }: BreadcrumbsProps) {
  return (
    <nav className="dpr-crumbs" aria-label={label}>
      <ol className="dpr-crumbs__list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;
          return (
            <li
              className={`dpr-crumbs__item${isLast ? ' dpr-crumbs__item--current' : ''}`}
              key={`${index}-${String(item.label)}`}
            >
              {index > 0 ? (
                <span className="dpr-crumbs__sep" aria-hidden="true">
                  ›
                </span>
              ) : null}
              {isLast ? (
                <span className="dpr-crumbs__label" aria-current="page">
                  {item.label}
                </span>
              ) : item.href ? (
                <a className="dpr-crumbs__link" href={item.href}>
                  {item.label}
                </a>
              ) : item.onSelect ? (
                <button type="button" className="dpr-crumbs__link" onClick={item.onSelect}>
                  {item.label}
                </button>
              ) : (
                <span className="dpr-crumbs__label">{item.label}</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
